<template>
  <div ref="heatmap" class="w-full" :style="`height: ${height}px`"></div>
</template>

<script setup>
import { ref, watch } from "vue";
import { isEmpty, uniq, first, last } from "lodash";
import { scaleBand, scaleQuantile } from "d3-scale";
import { schemeBlues } from "d3-scale-chromatic";
import { axisBottom, axisLeft } from "d3-axis";
import * as selection from "d3-selection";
import { mk } from "../../../lib/utils";

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  }, height: {
    type: [String, Number],
    default: 780
  }
});

const heatmap = ref(null);

watch(() => props.data, () => {
  if (!isEmpty(props.data)) {
    drawHeatmap();
  }
}, {
  immediate: true,
  deep: true
});

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
}

function getTextColor(backgroundColor) {
  const [r, g, b] = hexToRgb(backgroundColor);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 186 ? last(schemeBlues[9]) : first(schemeBlues[9]);
}

function drawHeatmap () {
  // clear before drawing
  selection.select(heatmap.value).selectChildren("*").remove();

  const margin = { top: 0, right: 0, bottom: 150, left: 150 };
  const origins = uniq(props.data.map((d) => d.origin_name));
  const destinations = uniq(props.data.map((d) => d.destination_name));
  const totals = uniq(props.data.map((d) => d.total));
  const width = selection.select(heatmap.value).node().getBoundingClientRect().width;
  const svg = selection.select(heatmap.value).append("svg").attr("height", "100%").attr("width", "100%");
  const x = scaleBand().domain(destinations).range([0, width - margin.left]).padding(0.05);
  const y = scaleBand().domain(origins).range([props.height - margin.bottom, 0]).padding(0.05);
  const colorScale = scaleQuantile().domain(totals).range(schemeBlues[9]);

  // Y labels
    svg.append("g").attr("transform", `translate(${(width - margin.left) / 2}, ${props.height - 30})`)
    .append("text")
    .style("font-size", 18)
    .style("text-rendering", "optimizeLegibility")
    .style("font-family", "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif")
    .style("fill", "#E4E4E7")
    .style("font-weight", "bold")
    .text("Destination Zone");
  const yLabels = svg.append("g")
    .style("font-size", 10)
    .style("text-rendering", "optimizeLegibility")
    .style("font-family", "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif")
    .attr("transform", `translate(${margin.left}, ${props.height - margin.bottom + margin.top})`)
    .call(axisBottom(x).tickSize(0));
  yLabels.select(".domain").remove();
  yLabels.selectAll("text")
    .style("text-anchor", "end")
    .attr("transform", "rotate(-65)");

    // X labels
    svg.append("g").attr("transform", `translate(30, ${props.height / 2 - 50})`)
    .append("text")
    .style("font-size", "18px")
    .style("text-rendering", "optimizeLegibility")
    .style("fill", "#E4E4E7")
    .style("font-weight", "bold")
    .attr("transform", "rotate(-90)")
    .text("Origin Zone");
  svg.append("g")
    .style("font-size", 10)
    .append("g").attr("transform", `translate(${margin.left}, ${margin.top})`)
    .call(axisLeft(y).tickSize(0))
    .select(".domain").remove();

  // matrix
  const matrixContainer = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`);
  matrixContainer.selectAll().data(props.data).enter()
    .append("rect")
    .attr("x", (d) => x(d.destination_name))
    .attr("y", (d) => y(d.origin_name))
    .attr("rx", 4)
    .attr("ry", 4)
    .attr("width", x.bandwidth())
    .attr("height", () => y.bandwidth())
    .style("fill", (d) => colorScale(d.total))
    .style("stroke-width", 4)
    .style("stroke", "none")
    .style("opacity", 1.0);
  matrixContainer.selectAll().data(props.data).enter()
    .append("text")
    .attr("x", (d) => x(d.destination_name) + x.bandwidth() / 2)
    .attr("y", (d) => y(d.origin_name) + y.bandwidth() / 2 + 5)
    .style("fill", (d) => getTextColor(colorScale(d.total)))
    .style("font-size", 12)
    .style("text-rendering", "optimizeLegibility")
    .style("font-family", "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif")
    .style("text-anchor", "middle")
    .text((d) => mk(d.total));
}
</script>
